import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { fmt12 } from "../lib/utils";
import { toast } from "sonner";
import { Plus, Send, Trash2, Users, Calendar, ShieldCheck, FileText, Eye, Download, Phone, Mail, FileDown, BellRing, Search, Tag, Trophy, MailPlus, ChevronLeft, ChevronRight, ChevronDown, PawPrint } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const DEFAULT_ROLES = ["Dog Walker", "Kennel Assistant", "Adoption Event", "Feeding", "Medical Care"];

const DEFAULT_PRESET_ROLE = "Paw Crew - Help with any and all shelter needs";

const INTEREST_LABELS = { general: "General", cans: "Cans for Canines", events: "Events" };
const renderInterests = (list) => (list || []).filter(i => INTEREST_LABELS[i]).map(i => (
  <span key={`i-${i}`} className="tag-pill tag-yellow !text-[9px] !py-0">{INTEREST_LABELS[i]}</span>
));
// Skills are free-text — admins see exactly what the volunteer typed.
const renderSkills = (list) => (list || []).filter(Boolean).map((s, idx) => (
  <span key={`s-${idx}-${s}`} className="tag-pill !bg-[color:var(--ink)]/10 !text-[color:var(--ink)] !text-[9px] !py-0">{s}</span>
));

const DEFAULT_SLOTS = {
  morning1: { title: "Morning Shift 1", start_time: "07:00", end_time: "09:00", role: DEFAULT_PRESET_ROLE },
  morning2: { title: "Morning Shift 2", start_time: "09:00", end_time: "11:00", role: DEFAULT_PRESET_ROLE },
  evening:  { title: "Evening Shift",   start_time: "16:30", end_time: "18:30", role: DEFAULT_PRESET_ROLE },
};

const SLOT_LABELS = { morning1: "Morning 1", morning2: "Morning 2", evening: "Evening", custom: "Custom" };

const EMPTY = { slot: "morning1", role: DEFAULT_PRESET_ROLE, date: "", location: "3910 Heron Drive, Hood River, OR 97031", max_volunteers: 4, description: "", urgent: false, repeat_frequency: "once", repeat_count: 1, custom_title: "", custom_start: "12:00", custom_end: "14:00", event_split_minutes: 0 };
const BULK_EMPTY = { start_date: "", days: 7, location: "3910 Heron Drive, Hood River, OR 97031", max_volunteers: 4, include_morning1: true, include_morning2: true, include_evening: true };

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [rolesText, setRolesText] = useState("");
  const [presets, setPresets] = useState(DEFAULT_SLOTS);
  const [presetsBusy, setPresetsBusy] = useState(false);
  const [create, setCreate] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState(BULK_EMPTY);
  const [broadcast, setBroadcast] = useState(null); // shift
  const [bcMode, setBcMode] = useState("all"); // "all" | "some"
  const [bcIncludeMe, setBcIncludeMe] = useState(true);
  const [bcSelected, setBcSelected] = useState(new Set());
  const [msg, setMsg] = useState({ subject: "", message: "" });
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiver, setWaiver] = useState({ title: "", intro: "", clausesText: "", has_reference_pdf: false, reference_pdf_filename: "" });
  const [viewVol, setViewVol] = useState(null);
  const [volSearch, setVolSearch] = useState("");
  const [showAllShifts, setShowAllShifts] = useState(false);
  const [adminTab, setAdminTab] = useState("volunteers");
  const [busy, setBusy] = useState(false);
  const [volSort, setVolSort] = useState("name-asc"); // name-asc | name-desc | shifts-desc | shifts-asc | recent | admins-first
  const [volFilter, setVolFilter] = useState("all"); // all | admins | missing-waiver | expiring-waiver | no-emergency
  const [leaderboard, setLeaderboard] = useState([]);
  const [paperWaiverFor, setPaperWaiverFor] = useState(null);
  const [paperWaiverScan, setPaperWaiverScan] = useState(null);
  const [paperWaiverBusy, setPaperWaiverBusy] = useState(false);
  const [kennelFor, setKennelFor] = useState(null);
  const [kennelForm, setKennelForm] = useState({ dog_name: "", dog_notes: "" });
  const [kennelPhoto, setKennelPhoto] = useState(null);
  const [kennelBusy, setKennelBusy] = useState(false);
  const [lbTotals, setLbTotals] = useState({ hours: 0, shifts: 0, volunteers: 0 });
  const [lbMonth, setLbMonth] = useState(() => {
    // current YYYY-MM in local time
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [lbPeriod, setLbPeriod] = useState("month"); // "month" | "all"
  const [lbBusy, setLbBusy] = useState(false);
  const [lbExpanded, setLbExpanded] = useState({}); // user_id -> bool

  const todayStr = new Date().toISOString().split("T")[0];
  const visibleShifts = showAllShifts ? shifts : shifts.filter((s) => s.date >= todayStr);

  const filteredVolunteers = (() => {
    let arr = volunteers.filter((v) => {
      // filter chip
      if (volFilter === "admins" && v.role !== "admin") return false;
      if (volFilter === "interest-general" && !(v.interests || []).includes("general")) return false;
      if (volFilter === "interest-cans" && !(v.interests || []).includes("cans")) return false;
      if (volFilter === "interest-events" && !(v.interests || []).includes("events")) return false;
      if (volFilter === "no-interest" && (v.interests || []).length > 0) return false;
      if (volFilter === "missing-waiver" && v.waiver_signed) return false;
      if (volFilter === "expiring-waiver" && !(v.waiver?.is_expiring_soon || v.waiver?.is_expired)) return false;
      if (volFilter === "no-emergency" && !!v.waiver?.emergency_contact_name) return false;
      // search
      const q = volSearch.trim().toLowerCase();
      if (!q) return true;
      return (
        (v.name || "").toLowerCase().includes(q) ||
        (v.email || "").toLowerCase().includes(q) ||
        (v.phone || "").toLowerCase().includes(q) ||
        (v.waiver?.emergency_contact_name || "").toLowerCase().includes(q) ||
        (v.waiver?.emergency_contact_phone || "").toLowerCase().includes(q)
      );
    });
    const nameOf = (v) => (v.name || v.email || "").toLowerCase();
    if (volSort === "name-asc") arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    else if (volSort === "name-desc") arr.sort((a, b) => nameOf(b).localeCompare(nameOf(a)));
    else if (volSort === "shifts-desc") arr.sort((a, b) => (b.signup_count || 0) - (a.signup_count || 0));
    else if (volSort === "shifts-asc") arr.sort((a, b) => (a.signup_count || 0) - (b.signup_count || 0));
    else if (volSort === "recent") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (volSort === "admins-first") arr.sort((a, b) => (a.role === "admin" ? -1 : 1) - (b.role === "admin" ? -1 : 1));
    return arr;
  })();

  const downloadVolWaiver = async (v) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/admin/volunteers/${v.user_id}/waiver-pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `waiver-${v.name.replace(/\s+/g,'-')}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download waiver."); }
  };

  const exportCsv = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/admin/volunteers.csv`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `hraad-volunteers-${new Date().toISOString().split("T")[0]}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Roster exported.");
    } catch { toast.error("Could not export CSV."); }
  };

  const sendBackfillReminders = async () => {
    const missing = volunteers.filter(v => v.waiver_signed && !v.waiver?.emergency_contact_name);
    if (missing.length === 0) { toast.success("Every signed volunteer already has an emergency contact."); return; }
    if (!window.confirm(`Send a reminder email + in-app notification to ${missing.length} volunteer${missing.length === 1 ? "" : "s"} who are missing an emergency contact?`)) return;
    try {
      const { data } = await api.post("/admin/backfill-emergency-reminders");
      toast.success(`Reminded ${data.sent} volunteer${data.sent === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    const lbQ = lbPeriod === "all" ? "period=all&limit=10" : `period=month&month=${lbMonth}&limit=10`;
    const [st, sh, v, r, p, lb] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/shifts"),
      api.get("/admin/volunteers"),
      api.get("/shifts/roles"),
      api.get("/shifts/presets"),
      api.get(`/admin/leaderboard?${lbQ}`),
    ]);
    setStats(st.data); setShifts(sh.data); setVolunteers(v.data);
    if (r.data?.roles?.length) setRoles(r.data.roles);
    if (p.data?.presets) setPresets(p.data.presets);
    setLeaderboard(lb.data?.leaders || []);
    setLbTotals(lb.data?.totals || { hours: 0, shifts: 0, volunteers: 0 });
  };
  useEffect(() => { load(); }, []);

  const refreshLeaderboard = async (period, monthStr) => {
    setLbBusy(true);
    try {
      const q = period === "all" ? "period=all&limit=10" : `period=month&month=${monthStr}&limit=10`;
      const { data } = await api.get(`/admin/leaderboard?${q}`);
      setLeaderboard(data.leaders || []);
      setLbTotals(data.totals || { hours: 0, shifts: 0, volunteers: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLbBusy(false); }
  };

  const shiftMonth = (delta) => {
    const [y, m] = lbMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const nm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setLbMonth(nm);
    setLbPeriod("month");
    setLbExpanded({});
    refreshLeaderboard("month", nm);
  };

  const setPeriodAll = () => {
    setLbPeriod("all");
    setLbExpanded({});
    refreshLeaderboard("all", lbMonth);
  };
  const setPeriodMonth = () => {
    setLbPeriod("month");
    setLbExpanded({});
    refreshLeaderboard("month", lbMonth);
  };

  const monthLabel = (() => {
    if (lbPeriod === "all") return "All time";
    const [y, m] = lbMonth.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
  })();
  const todayYm = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const atCurrentMonth = lbMonth === todayYm;

  const savePresets = async (next) => {
    setPresetsBusy(true);
    try {
      const { data } = await api.put("/admin/shift-presets", next);
      setPresets(data.presets);
      toast.success("Shift times saved.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPresetsBusy(false); }
  };

  const saveRoles = async () => {
    const list = rolesText.split("\n").map(s => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Add at least one role."); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/admin/shift-roles", { roles: list });
      setRoles(data.roles);
      toast.success(`Saved ${data.roles.length} role${data.roles.length === 1 ? "" : "s"}.`);
      setRolesOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const createShift = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const isCustom = form.slot === "custom";
      if (isCustom && !form.custom_title.trim()) {
        toast.error("Please give the special-event shift a title.");
        setBusy(false); return;
      }
      const payload = {
        title: isCustom ? form.custom_title.trim() : presets[form.slot].title,
        role: form.role,
        date: form.date,
        start_time: isCustom ? form.custom_start : presets[form.slot].start_time,
        end_time: isCustom ? form.custom_end : presets[form.slot].end_time,
        location: form.location,
        max_volunteers: Number(form.max_volunteers),
        description: form.description,
        urgent: form.urgent,
        repeat_frequency: form.repeat_frequency,
        repeat_count: Number(form.repeat_count),
      };
      if (isCustom && Number(form.event_split_minutes) > 0) {
        payload.event_slot_minutes = Number(form.event_split_minutes);
      }
      const { data } = await api.post("/shifts", payload);
      toast.success(data.count > 1 ? `Created ${data.count} shifts.` : "Shift created.");
      setCreate(false); setForm(EMPTY); load();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const bulkGenerate = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/shifts/generate-schedule", {
        ...bulk,
        days: Number(bulk.days),
        max_volunteers: Number(bulk.max_volunteers),
      });
      toast.success(`Generated ${data.count} shifts.`);
      setBulkOpen(false); setBulk(BULK_EMPTY); load();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const del = async (shift_id) => {
    if (!window.confirm("Delete this shift and all sign-ups?")) return;
    try { await api.delete(`/shifts/${shift_id}`); toast.success("Shift deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sendBroadcast = async () => {
    if (!broadcast || !msg.subject.trim() || !msg.message.trim()) return;
    setBusy(true);
    try {
      const payload = { shift_id: broadcast.shift_id, subject: msg.subject, message: msg.message, include_me: bcIncludeMe };
      if (bcMode === "some") payload.user_ids = Array.from(bcSelected);
      const { data } = await api.post("/admin/broadcast", payload);
      const parts = [`${data.recipients} email${data.recipients === 1 ? "" : "s"} queued`];
      if (data.skipped_invalid_email) parts.push(`${data.skipped_invalid_email} skipped (invalid address)`);
      if (data.skipped_unsubscribed) parts.push(`${data.skipped_unsubscribed} unsubscribed`);
      parts.push(`${data.total_notified_in_app} in-app notification${data.total_notified_in_app === 1 ? "" : "s"}`);
      toast.success(parts.join(" · "));
      setBroadcast(null); setMsg({ subject: "", message: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveWaiver = async () => {
    const clauses = waiver.clausesText.split("\n").map(s => s.trim()).filter(Boolean);
    if (!waiver.title.trim() || clauses.length === 0) {
      toast.error("Give the waiver a title and at least one clause.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/waiver-template", { title: waiver.title.trim(), intro: waiver.intro.trim(), clauses });
      toast.success(`Waiver updated. New volunteers will see ${clauses.length} clause${clauses.length === 1 ? "" : "s"}.`);
      setWaiverOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadWaiverPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("PDF must be under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/waiver-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWaiver((w) => ({ ...w, has_reference_pdf: true, reference_pdf_filename: file.name }));
      toast.success(`Uploaded ${file.name}. Volunteers can now view it on the waiver page.`);
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const removeWaiverPdf = async () => {
    if (!window.confirm("Remove the uploaded waiver PDF?")) return;
    setBusy(true);
    try {
      await api.delete("/admin/waiver-pdf");
      setWaiver((w) => ({ ...w, has_reference_pdf: false, reference_pdf_filename: "" }));
      toast.success("Reference PDF removed.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="tag-pill tag-red">Shelter Admin</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-3">Ops HQ.</h1>
            <p className="text-[color:var(--stone)] mt-2">Create shifts, review sign-ups, and broadcast urgent needs.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-edit-roles-btn" onClick={()=>{ setRolesText(roles.join("\n")); setRolesOpen(true); }} className="btn-pill btn-ghost-ink flex items-center gap-2 text-sm">
              <Tag className="h-4 w-4" /> Edit roles
            </button>
            <button data-testid="admin-edit-waiver-btn" onClick={async ()=>{
              try {
                const { data } = await api.get("/waiver/template");
                setWaiver({ title: data.title || "", intro: data.intro || "", clausesText: (data.clauses || []).join("\n"), has_reference_pdf: !!data.has_reference_pdf, reference_pdf_filename: data.reference_pdf_filename || "" });
                setWaiverOpen(true);
              } catch (e) { toast.error(formatApiError(e)); }
            }} className="btn-pill btn-ghost-ink flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4" /> Edit waiver
            </button>
            <button data-testid="admin-bulk-generate-btn" onClick={()=>setBulkOpen(true)} className="btn-pill btn-ghost-ink flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4" /> Generate week
            </button>
            <button data-testid="admin-create-shift-btn" onClick={()=>setCreate(true)} className="btn-pill btn-primary flex items-center gap-2 text-sm">
              <Plus className="h-4 w-4" /> New shift
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-4 gap-4 mb-8">
            {[
              { icon: <Users className="h-5 w-5" />, k: stats.volunteers, v: "Volunteers" },
              { icon: <ShieldCheck className="h-5 w-5" />, k: stats.signed_waivers, v: "Signed waivers" },
              { icon: <Calendar className="h-5 w-5" />, k: stats.shifts, v: "Total shifts" },
              { icon: <Users className="h-5 w-5" />, k: stats.upcoming_signups, v: "Total sign-ups" },
            ].map((c, i) => (
              <div key={c.v} className="card-warm p-5" data-testid={`admin-stat-${i}`}>
                <div className="h-10 w-10 rounded-full bg-[color:var(--cream-2)] flex items-center justify-center mb-3">{c.icon}</div>
                <div className="font-display text-3xl">{c.k}</div>
                <div className="text-xs text-[color:var(--stone)] uppercase tracking-wide mt-1">{c.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Hours leaderboard */}
        <div className="card-warm p-6 mb-8" data-testid="admin-leaderboard">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[color:var(--yellow)]/60 flex items-center justify-center">
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-xl">Hours leaderboard</div>
                <div className="text-xs text-[color:var(--stone)]">Top volunteers by auto-logged hours · tap a row to see the shifts they worked</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Period toggle */}
              <div className="inline-flex rounded-full border-2 border-[color:var(--border)] p-0.5">
                <button
                  data-testid="lb-period-month"
                  onClick={setPeriodMonth}
                  className={`px-3 py-1 text-xs font-semibold rounded-full ${lbPeriod === "month" ? "bg-[color:var(--ink)] text-[color:var(--cream)]" : "text-[color:var(--stone)] hover:text-[color:var(--ink)]"}`}
                >Monthly</button>
                <button
                  data-testid="lb-period-all"
                  onClick={setPeriodAll}
                  className={`px-3 py-1 text-xs font-semibold rounded-full ${lbPeriod === "all" ? "bg-[color:var(--ink)] text-[color:var(--cream)]" : "text-[color:var(--stone)] hover:text-[color:var(--ink)]"}`}
                >All time</button>
              </div>
              {/* Month stepper — only when Monthly is active */}
              {lbPeriod === "month" && (
                <div className="inline-flex items-center gap-1">
                  <button
                    data-testid="lb-prev-month"
                    onClick={() => shiftMonth(-1)}
                    disabled={lbBusy}
                    className="p-1.5 rounded-full border-2 border-[color:var(--border)] hover:border-[color:var(--ink)] disabled:opacity-40"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div data-testid="lb-month-label" className="font-display text-sm min-w-[140px] text-center">{monthLabel}</div>
                  <button
                    data-testid="lb-next-month"
                    onClick={() => shiftMonth(1)}
                    disabled={lbBusy || atCurrentMonth}
                    title={atCurrentMonth ? "You're on the current month" : "Next month"}
                    className="p-1.5 rounded-full border-2 border-[color:var(--border)] hover:border-[color:var(--ink)] disabled:opacity-40"
                    aria-label="Next month"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Totals summary */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-2xl bg-[color:var(--cream-2)]/50 p-3 text-center" data-testid="lb-total-hours">
              <div className="font-display text-2xl">{lbTotals.hours}h</div>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] mt-0.5">Total hours</div>
            </div>
            <div className="rounded-2xl bg-[color:var(--cream-2)]/50 p-3 text-center" data-testid="lb-total-shifts">
              <div className="font-display text-2xl">{lbTotals.shifts}</div>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] mt-0.5">Shifts logged</div>
            </div>
            <div className="rounded-2xl bg-[color:var(--cream-2)]/50 p-3 text-center" data-testid="lb-total-volunteers">
              <div className="font-display text-2xl">{lbTotals.volunteers}</div>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] mt-0.5">Volunteers</div>
            </div>
          </div>

          {leaderboard.length === 0 ? (
            <div className="text-sm text-[color:var(--stone)] italic py-6 text-center" data-testid="admin-leaderboard-empty">
              {lbPeriod === "all" ? "No completed shifts on record yet." : `No completed shifts in ${monthLabel}.`}
            </div>
          ) : (
            <ol className="space-y-2">
              {leaderboard.map((row, i) => {
                const medals = ["\u{1F947}", "\u{1F948}", "\u{1F949}"]; // gold, silver, bronze
                const badge = medals[i] || `${i + 1}.`;
                const expanded = !!lbExpanded[row.user_id];
                const toggle = () => setLbExpanded({ ...lbExpanded, [row.user_id]: !expanded });
                return (
                  <li key={row.user_id} className="rounded-2xl bg-[color:var(--cream-2)]/40 hover:bg-[color:var(--cream-2)]" data-testid={`admin-leaderboard-row-${i}`}>
                    <button
                      type="button"
                      onClick={toggle}
                      data-testid={`admin-leaderboard-toggle-${i}`}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left"
                      aria-expanded={expanded}
                    >
                      <div className="text-xl w-8 text-center shrink-0">{badge}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-base truncate flex items-center gap-2">
                          {row.name}
                          {row.is_admin && <span className="tag-pill tag-red !text-[9px] !py-0">Admin</span>}
                        </div>
                        <div className="text-xs text-[color:var(--stone)] truncate">{row.email}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display text-lg">{row.hours}h</div>
                        <div className="text-[10px] text-[color:var(--stone)] uppercase tracking-wide">{row.shifts} shift{row.shifts === 1 ? "" : "s"}</div>
                      </div>
                      <ChevronDown className={`h-4 w-4 text-[color:var(--stone)] shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </button>
                    {expanded && (
                      <div className="px-4 pb-4 pt-1" data-testid={`admin-leaderboard-shifts-${i}`}>
                        {(row.shift_log || []).length === 0 ? (
                          <div className="text-xs text-[color:var(--stone)] italic">No shift breakdown available.</div>
                        ) : (
                          <ul className="border-l-2 border-[color:var(--border)] pl-3 space-y-1.5 text-xs">
                            {(row.shift_log || []).map((sl) => (
                              <li key={sl.shift_id} className="flex items-center gap-2">
                                <span className="font-semibold text-[color:var(--ink)]">{sl.date}</span>
                                <span className="text-[color:var(--stone)]">{fmt12(sl.start_time)}–{fmt12(sl.end_time)}</span>
                                <span className="text-[color:var(--stone)]">·</span>
                                <span className="truncate">{sl.title}</span>
                                <span className="ml-auto font-display text-sm shrink-0">{sl.hours}h</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b-2 border-[color:var(--border)] mb-8">
          <button data-testid="admin-tab-volunteers" onClick={()=>setAdminTab("volunteers")} className={`px-5 py-3 font-display text-lg -mb-0.5 border-b-4 transition-colors ${adminTab === "volunteers" ? "border-[color:var(--red)] text-[color:var(--ink)]" : "border-transparent text-[color:var(--stone)] hover:text-[color:var(--ink)]"}`}>
            Volunteers <span className="ml-1 text-xs font-body text-[color:var(--stone)]">({volunteers.length})</span>
          </button>
          <button data-testid="admin-tab-shifts" onClick={()=>setAdminTab("shifts")} className={`px-5 py-3 font-display text-lg -mb-0.5 border-b-4 transition-colors ${adminTab === "shifts" ? "border-[color:var(--red)] text-[color:var(--ink)]" : "border-transparent text-[color:var(--stone)] hover:text-[color:var(--ink)]"}`}>
            Shifts <span className="ml-1 text-xs font-body text-[color:var(--stone)]">({shifts.length})</span>
          </button>
        </div>

        {/* Volunteers tab */}
        {adminTab === "volunteers" && (
          <div data-testid="admin-volunteers-panel">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl">Volunteers</h2>
            <p className="text-xs text-[color:var(--stone)] mt-1">Full contact info, waiver status, and emergency contact for every volunteer.</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--stone)]" />
              <input data-testid="admin-volunteer-search" value={volSearch} onChange={(e)=>setVolSearch(e.target.value)} placeholder="Search name, email, phone…" className="pl-9 pr-4 py-2 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white text-sm w-64" />
            </div>
            <select data-testid="admin-volunteer-filter" value={volFilter} onChange={(e)=>setVolFilter(e.target.value)} className="px-4 py-2 rounded-full border-2 border-[color:var(--border)] outline-none bg-white text-sm">
              <option value="all">Everyone</option>
              <option value="admins">Admins only</option>
              <option value="interest-general">Interested: General shelter</option>
              <option value="interest-cans">Interested: Cans for Canines</option>
              <option value="interest-events">Interested: Special events</option>
              <option value="no-interest">Haven&rsquo;t picked interests</option>
              <option value="missing-waiver">Missing waiver</option>
              <option value="expiring-waiver">Expiring or expired waiver</option>
              <option value="no-emergency">No emergency contact</option>
            </select>
            <select data-testid="admin-volunteer-sort" value={volSort} onChange={(e)=>setVolSort(e.target.value)} className="px-4 py-2 rounded-full border-2 border-[color:var(--border)] outline-none bg-white text-sm">
              <option value="name-asc">Sort: Name (A→Z)</option>
              <option value="name-desc">Sort: Name (Z→A)</option>
              <option value="shifts-desc">Sort: Most shifts</option>
              <option value="shifts-asc">Sort: Fewest shifts</option>
              <option value="recent">Sort: Recently joined</option>
              <option value="admins-first">Sort: Admins first</option>
            </select>
            <button data-testid="admin-backfill-btn" onClick={sendBackfillReminders} className="btn-pill btn-ghost-ink text-sm inline-flex items-center gap-2">
              <BellRing className="h-4 w-4" /> Nudge missing emergency
            </button>
            <button
              data-testid="admin-bulk-waiver-invite-btn"
              onClick={async () => {
                if (!window.confirm("Email every volunteer whose waiver is missing or expired? They'll get a one-click link to sign or re-sign.")) return;
                setBusy(true);
                try {
                  const { data } = await api.post("/admin/send-bulk-waiver-invites");
                  toast.success(data.sent ? `Sent ${data.sent} waiver invite${data.sent === 1 ? "" : "s"}.` : "No volunteers currently need to sign — everyone's cleared.");
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setBusy(false); }
              }}
              disabled={busy}
              className="btn-pill btn-ghost-ink text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <MailPlus className="h-4 w-4" /> Bulk waiver invite
            </button>
            <button data-testid="admin-export-csv-btn" onClick={exportCsv} className="btn-pill btn-primary text-sm inline-flex items-center gap-2">
              <FileDown className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>
        <div className="card-warm overflow-hidden mb-12">
          <div className="grid grid-cols-12 gap-2 px-6 py-3 text-xs uppercase tracking-wide font-semibold text-[color:var(--stone)] border-b border-[color:var(--border)]">
            <div className="col-span-2">Name / Email</div>
            <div className="col-span-1">Phone</div>
            <div className="col-span-1">Interests</div>
            <div className="col-span-2">Skills</div>
            <div className="col-span-2">Emergency contact</div>
            <div className="col-span-2">Waiver</div>
            <div className="col-span-1 text-center">Shifts</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {volunteers.length === 0 ? (
            <div className="p-8 text-center text-[color:var(--stone)] text-sm">No volunteers signed up yet.</div>
          ) : filteredVolunteers.length === 0 ? (
            <div className="p-8 text-center text-[color:var(--stone)] text-sm" data-testid="admin-vol-no-match">No volunteers match &ldquo;{volSearch}&rdquo;.</div>
          ) : filteredVolunteers.map(v => (
            <div key={v.user_id} className="grid grid-cols-12 gap-2 px-6 py-3 items-center border-b border-[color:var(--border)] last:border-b-0 text-sm" data-testid={`admin-volunteer-row-${v.user_id}`}>
              <div className="col-span-2">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  <span className="truncate">{v.name}</span>
                  {v.role === "admin" && <span className="tag-pill tag-red !text-[9px] !py-0" data-testid={`admin-badge-${v.user_id}`}>Admin</span>}
                  {v.is_minor && <span className="tag-pill tag-red !text-[9px] !py-0" data-testid={`admin-minor-flag-${v.user_id}`}>Minor</span>}
                </div>
                <a href={`mailto:${v.email}`} className="text-xs text-[color:var(--stone)] hover:text-[color:var(--red)] break-all">{v.email}</a>
                {typeof v.age === "number" && v.age >= 0 && (
                  <div className="text-[10px] text-[color:var(--stone)] mt-0.5">Age {v.age}</div>
                )}
              </div>
              <div className="col-span-1 text-[color:var(--stone)]">
                {v.phone ? <a href={`tel:${v.phone}`} className="hover:text-[color:var(--ink)] text-xs break-all">{v.phone}</a> : <span className="opacity-50">—</span>}
              </div>
              <div className="col-span-1 flex flex-wrap gap-1" data-testid={`vol-interests-cell-${v.user_id}`}>
                {(v.interests || []).length === 0 ? (
                  <span className="text-xs text-[color:var(--stone)] opacity-50">—</span>
                ) : renderInterests(v.interests)}
              </div>
              <div className="col-span-2 flex flex-wrap gap-1" data-testid={`vol-skills-cell-${v.user_id}`}>
                {(v.skills || []).length === 0 ? (
                  <span className="text-xs text-[color:var(--stone)] opacity-50">—</span>
                ) : renderSkills(v.skills)}
              </div>
              <div className="col-span-2 text-[color:var(--stone)]">
                {v.waiver?.emergency_contact_name ? (
                  <>
                    <div className="text-[color:var(--ink)] font-medium truncate">{v.waiver.emergency_contact_name}</div>
                    <a href={`tel:${v.waiver.emergency_contact_phone}`} className="text-xs hover:text-[color:var(--red)]">{v.waiver.emergency_contact_phone}</a>
                  </>
                ) : <span className="opacity-60 text-xs italic">Not on file</span>}
              </div>
              <div className="col-span-2">
                {v.waiver_signed ? (
                  <>
                    <span className={`tag-pill !text-[10px] ${v.waiver?.is_expired ? "tag-red" : v.waiver?.is_expiring_soon ? "tag-yellow" : "tag-forest"}`}>
                      {v.waiver?.is_expired ? "Expired" : v.waiver?.is_expiring_soon ? `${v.waiver.days_until_expiry}d left` : "Signed"}
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {v.paper_waiver_received && (
                        <>
                          <span className="tag-pill !text-[9px] !py-0 bg-[color:var(--yellow)]/40 text-[color:var(--ink)]" data-testid={`vol-paper-flag-${v.user_id}`}>Paper</span>
                          <button
                            data-testid={`vol-paper-download-${v.user_id}`}
                            onClick={async () => {
                              try {
                                const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                                const res = await fetch(`${BACKEND_URL}/api/admin/volunteers/${v.user_id}/paper-waiver-scan`, { credentials: "include" });
                                if (!res.ok) { toast.error("No scanned copy on file for this volunteer."); return; }
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                window.open(url, "_blank");
                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                              } catch { toast.error("Could not open scan."); }
                            }}
                            className="text-[9px] font-semibold text-[color:var(--ink)] underline"
                          >View scan</button>
                        </>
                      )}
                      {v.is_minor && <span className="tag-pill tag-red !text-[9px] !py-0" data-testid={`vol-minor-flag-${v.user_id}`}>Minor</span>}
                    </div>
                    <div className="text-[10px] text-[color:var(--stone)] mt-1">{v.waiver?.signed_at ? new Date(v.waiver.signed_at).toLocaleDateString() : ""}</div>
                  </>
                ) : (
                  <>
                    <span className="tag-pill tag-red !text-[10px]">Missing</span>
                    {v.is_minor && (
                      <div className="mt-1">
                        <span className="tag-pill tag-red !text-[9px] !py-0" data-testid={`vol-minor-flag-${v.user_id}`}>Minor</span>
                        <button
                          data-testid={`vol-paper-receive-btn-${v.user_id}`}
                          onClick={() => setPaperWaiverFor(v)}
                          className="mt-1 block text-[10px] font-semibold text-[color:var(--red)] hover:underline"
                        >Mark paper waiver received</button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="col-span-1 text-center font-semibold">{v.signup_count}</div>
              <div className="col-span-1 text-right flex justify-end gap-1">
                <button data-testid={`admin-view-volunteer-${v.user_id}`} onClick={()=>setViewVol(v)} className="btn-pill text-xs inline-flex items-center gap-1 bg-[color:var(--ink)] text-[color:var(--cream)] hover:opacity-90">
                  <Eye className="h-3.5 w-3.5" /> View
                </button>
                <button
                  data-testid={`admin-delete-volunteer-${v.user_id}`}
                  onClick={async () => {
                    if (v.user_id === user?.user_id) { toast.error("You can't delete your own account here."); return; }
                    if (!window.confirm(`Permanently delete ${v.name || v.email}?\n\nThis removes their waiver, signups (${v.signup_count || 0}) and notifications. This can't be undone.`)) return;
                    try {
                      const { data } = await api.delete(`/admin/volunteers/${v.user_id}`);
                      toast.success(`Deleted ${data.deleted}${data.signups_removed ? ` (${data.signups_removed} signup${data.signups_removed === 1 ? "" : "s"} removed)` : ""}.`);
                      await load();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className="p-2 rounded-full bg-[color:var(--red)]/10 text-[color:var(--red)] hover:bg-[color:var(--red)]/20"
                  title="Delete volunteer"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        </div>
        )}

        {/* Shifts tab */}
        {adminTab === "shifts" && (
        <div data-testid="admin-shifts-panel" className="mb-10">
          <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h2 className="font-display text-2xl">Shifts</h2>
              <p className="text-xs text-[color:var(--stone)] mt-1">{showAllShifts ? "Showing every shift, past and future." : "Showing upcoming shifts only."}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                data-testid="admin-send-reminders-now-btn"
                onClick={async () => {
                  if (!window.confirm("Send shift reminders (email + in-app) to everyone signed up for tomorrow's shifts right now?")) return;
                  setBusy(true);
                  try {
                    const { data } = await api.post("/admin/send-reminders-now");
                    toast.success(`Sent ${data.sent} shift reminder${data.sent === 1 ? "" : "s"}.`);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setBusy(false); }
                }}
                disabled={busy}
                className="btn-pill btn-ghost-ink text-xs inline-flex items-center gap-1 disabled:opacity-60"
              >
                <BellRing className="h-3.5 w-3.5" /> Send reminders now
              </button>
              <button
                data-testid="admin-send-waiver-nudges-btn"
                onClick={async () => {
                  if (!window.confirm("Email volunteers whose waiver expires in ~14 days or has already expired?")) return;
                  setBusy(true);
                  try {
                    const { data } = await api.post("/admin/send-waiver-nudges-now");
                    toast.success(`Sent ${data.sent} waiver nudge${data.sent === 1 ? "" : "s"}.`);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setBusy(false); }
                }}
                disabled={busy}
                className="btn-pill btn-ghost-ink text-xs inline-flex items-center gap-1 disabled:opacity-60"
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Nudge expiring waivers
              </button>
              <button
                data-testid="admin-migrate-legacy-btn"
                onClick={async () => {
                  if (!window.confirm("Clean up the old 'Morning Shift' (AM) rows? Their signups will be moved onto the matching 'Morning Shift 1' for the same day. This can't be undone.")) return;
                  setBusy(true);
                  try {
                    const { data } = await api.post("/admin/shifts/migrate-legacy");
                    const parts = [];
                    if (data.renamed) parts.push(`${data.renamed} renamed`);
                    if (data.merged) parts.push(`${data.merged} merged`);
                    if (data.signups_moved) parts.push(`${data.signups_moved} signups moved`);
                    toast.success(parts.length ? `Cleanup done: ${parts.join(", ")}.` : "No legacy AM shifts found.");
                    await load();
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setBusy(false); }
                }}
                disabled={busy}
                className="btn-pill btn-ghost-ink text-xs inline-flex items-center gap-1 disabled:opacity-60"
              >
                <Trash2 className="h-3.5 w-3.5" /> Clean up legacy AM shifts
              </button>
              <button data-testid="admin-toggle-shifts-btn" onClick={()=>setShowAllShifts((s)=>!s)} className="btn-pill btn-ghost-ink text-xs">
                {showAllShifts ? "Show upcoming only" : "Show all shifts"}
              </button>
            </div>
          </div>

          {/* Always-visible preset editor for the Shifts tab */}
          <div className="card-warm p-5 mb-4" data-testid="admin-presets-editor">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="font-display text-lg">Default shift slots</div>
                <p className="text-xs text-[color:var(--stone)]">Rename or re-time any slot. Changes update the calendar chips and any new shifts you create.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {["morning1", "morning2", "evening"].map((k) => {
                const p = presets[k];
                return (
                  <div key={k} className="rounded-2xl border-2 border-[color:var(--border)] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)]">{SLOT_LABELS[k]}</div>
                    <input
                      type="text"
                      data-testid={`preset-${k}-title`}
                      value={p.title}
                      disabled={presetsBusy}
                      onChange={(e) => setPresets({ ...presets, [k]: { ...p, title: e.target.value } })}
                      onBlur={(e) => { if (e.target.value.trim() && e.target.value !== presets[k].title) savePresets({ ...presets, [k]: { ...p, title: e.target.value.trim() } }); }}
                      placeholder="Shift name"
                      className="mt-1 w-full px-3 py-2 text-sm rounded-full border-2 border-[color:var(--border)] bg-white outline-none focus:border-[color:var(--ink)]"
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="time"
                        data-testid={`preset-${k}-start`}
                        value={p.start_time}
                        disabled={presetsBusy}
                        onChange={(e) => setPresets({ ...presets, [k]: { ...p, start_time: e.target.value } })}
                        onBlur={(e) => { if (e.target.value !== p.start_time) return; savePresets({ ...presets, [k]: { ...p, start_time: e.target.value } }); }}
                        className="w-full px-3 py-2 text-sm rounded-full border-2 border-[color:var(--border)] bg-white"
                      />
                      <span className="text-xs text-[color:var(--stone)]">–</span>
                      <input
                        type="time"
                        data-testid={`preset-${k}-end`}
                        value={p.end_time}
                        disabled={presetsBusy}
                        onChange={(e) => setPresets({ ...presets, [k]: { ...p, end_time: e.target.value } })}
                        onBlur={(e) => { if (e.target.value !== p.end_time) return; savePresets({ ...presets, [k]: { ...p, end_time: e.target.value } }); }}
                        className="w-full px-3 py-2 text-sm rounded-full border-2 border-[color:var(--border)] bg-white"
                      />
                    </div>
                    <div className="mt-2 text-[10px] text-[color:var(--stone)]">
                      {fmt12(p.start_time)} – {fmt12(p.end_time)}
                    </div>
                    <div className="mt-2">
                      <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Default role</label>
                      <select
                        data-testid={`preset-${k}-role`}
                        value={p.role || DEFAULT_PRESET_ROLE}
                        disabled={presetsBusy}
                        onChange={(e) => { const next = { ...presets, [k]: { ...p, role: e.target.value } }; setPresets(next); savePresets(next); }}
                        className="mt-1 w-full px-3 py-2 text-sm rounded-full border-2 border-[color:var(--border)] bg-white outline-none focus:border-[color:var(--ink)]"
                      >
                        {Array.from(new Set([p.role, DEFAULT_PRESET_ROLE, ...roles].filter(Boolean))).map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card-warm overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-6 py-3 text-xs uppercase tracking-wide font-semibold text-[color:var(--stone)] border-b border-[color:var(--border)]">
              <div className="col-span-4">Shift</div>
              <div className="col-span-2">Date/Time</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2">Sign-ups</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {visibleShifts.length === 0 ? (
              <div className="p-10 text-center text-[color:var(--stone)]" data-testid="admin-shifts-empty">No shifts to show.</div>
            ) : visibleShifts.map((s) => (
              <div key={s.shift_id} className="relative group grid grid-cols-12 gap-2 px-6 py-4 items-center border-b border-[color:var(--border)] last:border-b-0" data-testid={`admin-shift-row-${s.shift_id}`}>
                <div className="col-span-4">
                  <div className="font-display">{s.title} {s.urgent && <span className="tag-pill tag-red ml-2 !text-[10px] !py-0.5">Urgent</span>}</div>
                  <div className="text-xs text-[color:var(--stone)]">{s.location}</div>
                </div>
                <div className="col-span-2 text-sm">{s.date}<br/><span className="text-xs text-[color:var(--stone)]">{fmt12(s.start_time)}–{fmt12(s.end_time)}</span></div>
                <div className="col-span-2 text-sm">{s.role}</div>
                <div className="col-span-2 text-sm">
                  <div className="font-semibold">{s.signup_count} / {s.max_volunteers}</div>
                  {s.signups && s.signups.length > 0 && (
                    <div className="text-xs text-[color:var(--stone)] mt-1 truncate">
                      {s.signups.slice(0,2).map(su => su.volunteer?.name).join(", ")}{s.signups.length>2 && ` +${s.signups.length-2}`}
                    </div>
                  )}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button
                    data-testid={`admin-kennel-btn-${s.shift_id}`}
                    onClick={() => { setKennelFor(s); setKennelForm({ dog_name: s.kennel_card?.dog_name || "", dog_notes: s.kennel_card?.dog_notes || "" }); setKennelPhoto(null); }}
                    className={`p-2 rounded-full transition-colors ${s.kennel_card?.dog_name || s.kennel_card?.has_photo ? "bg-[color:var(--yellow)]/60 text-[color:var(--ink)]" : "bg-[color:var(--cream-2)] text-[color:var(--stone)] hover:text-[color:var(--ink)]"}`}
                    title={s.kennel_card?.dog_name ? `Kennel card: ${s.kennel_card.dog_name}` : "Attach a kennel card"}
                  >
                    <PawPrint className="h-4 w-4" />
                  </button>
                  <button data-testid={`admin-broadcast-btn-${s.shift_id}`} onClick={()=>{ setBroadcast(s); setMsg({ subject: `Volunteers needed: ${s.title}`, message: `We still have open spots for ${s.title} on ${s.date} (${fmt12(s.start_time)}–${fmt12(s.end_time)}). Can you help?` }); }} className="p-2 rounded-full bg-[color:var(--yellow)]/40 hover:bg-[color:var(--yellow)]" title="Notify volunteers">
                    <Send className="h-4 w-4" />
                  </button>
                  <button data-testid={`admin-delete-shift-${s.shift_id}`} onClick={()=>del(s.shift_id)} className="p-2 rounded-full bg-[color:var(--red)]/10 text-[color:var(--red)] hover:bg-[color:var(--red)]/20" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {s.signups && s.signups.length > 0 && (
                  <div className="absolute z-20 left-6 right-6 top-full mt-1 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity" data-testid={`admin-shift-preview-${s.shift_id}`}>
                    <div className="rounded-2xl bg-[color:var(--ink)] text-[color:var(--cream)] p-4 shadow-2xl max-w-md">
                      <div className="text-[10px] uppercase tracking-wider text-[color:var(--yellow)] mb-2">Signed up ({s.signups.length})</div>
                      <div className="space-y-2">
                        {s.signups.map(su => (
                          <div key={su.signup_id} className="text-sm">
                            <div className="font-semibold flex items-center gap-2">
                              {su.volunteer?.name || "—"}
                              {su.volunteer?.role === "admin" && <span className="tag-pill tag-red !text-[9px] !py-0">Admin</span>}
                            </div>
                            <div className="text-[color:var(--cream)]/70 text-xs flex flex-wrap gap-x-3">
                              {su.volunteer?.email && <span>{su.volunteer.email}</span>}
                              {su.volunteer?.phone && <span>{su.volunteer.phone}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Shifts */}
      </div>

      {/* Edit roles dialog */}
      <Dialog open={rolesOpen} onOpenChange={setRolesOpen}>
        <DialogContent data-testid="admin-roles-dialog" className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit shift roles</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[color:var(--stone)]">
              One role per line. These show up in the shift creation dropdowns. Existing shifts keep their current role even if you remove it here.
            </p>
            <textarea data-testid="admin-roles-textarea" value={rolesText} onChange={(e)=>setRolesText(e.target.value)} rows={10} className="w-full px-4 py-3 rounded-2xl border-2 border-[color:var(--border)] outline-none bg-white font-mono text-sm" placeholder={"Dog Walker\nKennel Assistant\nAdoption Event\nFeeding\nMedical Care"} />
            <div className="text-xs text-[color:var(--stone)]">
              {rolesText.split("\n").filter(s => s.trim()).length} role{rolesText.split("\n").filter(s => s.trim()).length === 1 ? "" : "s"} · Duplicates auto-removed.
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={()=>setRolesOpen(false)} className="btn-pill btn-ghost-ink text-sm">Cancel</button>
            <button data-testid="admin-roles-save-btn" onClick={saveRoles} disabled={busy} className="btn-pill btn-primary text-sm">{busy ? "Saving…" : "Save roles"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Volunteer detail dialog */}
      <Dialog open={!!viewVol} onOpenChange={(v)=>!v && setViewVol(null)}>
        <DialogContent data-testid="admin-volunteer-dialog" className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{viewVol?.name}</DialogTitle>
          </DialogHeader>
          {viewVol && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <a href={`mailto:${viewVol.email}`} className="p-3 rounded-2xl bg-[color:var(--cream-2)] hover:bg-[color:var(--yellow)]/30 transition-colors">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] flex items-center gap-1"><Mail className="h-3 w-3" /> Email</div>
                  <div className="text-sm font-semibold break-all mt-1">{viewVol.email}</div>
                </a>
                <a href={viewVol.phone ? `tel:${viewVol.phone}` : "#"} className={`p-3 rounded-2xl bg-[color:var(--cream-2)] ${viewVol.phone ? "hover:bg-[color:var(--yellow)]/30" : "opacity-60"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</div>
                  <div className="text-sm font-semibold mt-1">{viewVol.phone || "Not on file"}</div>
                </a>
              </div>

              <div className="p-4 rounded-2xl border-2 border-[color:var(--border)]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--stone)]">Emergency contact</div>
                  {viewVol.waiver?.emergency_contact_name && <span className="tag-pill tag-forest !text-[10px]">On file</span>}
                </div>
                {viewVol.waiver?.emergency_contact_name ? (
                  <>
                    <div className="font-semibold text-lg">{viewVol.waiver.emergency_contact_name}</div>
                    <a href={`tel:${viewVol.waiver.emergency_contact_phone}`} className="text-sm text-[color:var(--red)] hover:underline">{viewVol.waiver.emergency_contact_phone}</a>
                  </>
                ) : <div className="text-sm text-[color:var(--stone)]">Not on file. Ask them to complete the waiver.</div>}
              </div>

              <div className="p-4 rounded-2xl border-2 border-[color:var(--border)]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--stone)]">Waiver</div>
                  {viewVol.waiver_signed ? <span className="tag-pill tag-forest !text-[10px]">Signed</span> : <span className="tag-pill tag-red !text-[10px]">Missing</span>}
                </div>
                {viewVol.waiver_signed ? (
                  <>
                    <div className="text-sm">Signed as <span className="font-serif-display italic text-lg">{viewVol.waiver?.full_name}</span></div>
                    <div className="text-xs text-[color:var(--stone)] mt-1">on {viewVol.waiver?.signed_at ? new Date(viewVol.waiver.signed_at).toLocaleString() : "—"}</div>
                    <button data-testid={`admin-download-waiver-${viewVol.user_id}`} onClick={()=>downloadVolWaiver(viewVol)} className="mt-3 btn-pill btn-ghost-ink text-xs inline-flex items-center gap-2">
                      <Download className="h-3.5 w-3.5" /> Download signed waiver PDF
                    </button>
                  </>
                ) : <div className="text-sm text-[color:var(--stone)]">This volunteer hasn&rsquo;t signed the waiver yet and can&rsquo;t book shifts.</div>}
              </div>

              <div className="text-sm text-[color:var(--stone)]">
                Total shifts signed up: <span className="font-semibold text-[color:var(--ink)]">{viewVol.signup_count}</span>
              </div>

              <div className="p-4 rounded-2xl border-2 border-[color:var(--red)]/40 bg-[color:var(--red)]/5" data-testid="admin-role-block">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--stone)]">Admin access</div>
                    <div className="text-sm mt-1">
                      {viewVol.role === "admin"
                        ? "This person can create shifts, manage volunteers, and send broadcasts."
                        : "Grant admin access so this person can create shifts, manage volunteers, and send notifications."}
                    </div>
                  </div>
                  <button
                    data-testid={`admin-toggle-role-${viewVol.user_id}`}
                    disabled={busy || viewVol.user_id === user?.user_id}
                    onClick={async () => {
                      const target = viewVol.role === "admin" ? "volunteer" : "admin";
                      const label = target === "admin" ? "Make this person an admin?" : "Remove admin access from this person?";
                      if (!window.confirm(label)) return;
                      setBusy(true);
                      try {
                        await api.post(`/admin/volunteers/${viewVol.user_id}/set-role`, { role: target });
                        toast.success(target === "admin" ? "Admin access granted." : "Admin access removed.");
                        setViewVol({ ...viewVol, role: target });
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setBusy(false); }
                    }}
                    className={`btn-pill text-xs shrink-0 ${viewVol.role === "admin" ? "bg-[color:var(--red)]/15 text-[color:var(--red)] hover:bg-[color:var(--red)]/25" : "btn-primary"} disabled:opacity-50`}
                    title={viewVol.user_id === user?.user_id ? "You can't change your own role" : ""}
                  >
                    {viewVol.role === "admin" ? "Remove admin" : "Make admin"}
                  </button>
                </div>
                {viewVol.role === "admin" && (
                  <p className="text-[10px] text-[color:var(--stone)] mt-2">Admins keep their access until removed. Removing admin signs them out immediately.</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={()=>setViewVol(null)} className="btn-pill btn-ghost-ink text-sm">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kennel card dialog */}
      <Dialog open={!!kennelFor} onOpenChange={(o)=>{ if(!o){ setKennelFor(null); setKennelPhoto(null); } }}>
        <DialogContent data-testid="admin-kennel-dialog" className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Kennel card for this shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[color:var(--stone)]">
              Attach a photo + notes about the dog(s) for <span className="font-semibold text-[color:var(--ink)]">{kennelFor?.title} · {kennelFor?.date}</span>. Volunteers see this on the signup dialog so they know exactly who they&rsquo;re walking.
            </p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1">Dog name</label>
              <input
                data-testid="admin-kennel-dog-name"
                value={kennelForm.dog_name}
                onChange={(e)=>setKennelForm({...kennelForm, dog_name: e.target.value})}
                placeholder="e.g. Biscuit"
                className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] bg-white outline-none focus:border-[color:var(--ink)]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1">Notes</label>
              <textarea
                data-testid="admin-kennel-dog-notes"
                value={kennelForm.dog_notes}
                onChange={(e)=>setKennelForm({...kennelForm, dog_notes: e.target.value})}
                placeholder="Temperament, favourite routes, cues to use, feeding, meds…"
                rows={4}
                className="w-full px-4 py-3 rounded-2xl border-2 border-[color:var(--border)] bg-white outline-none focus:border-[color:var(--ink)] resize-y"
              />
            </div>
            <div className="p-3 rounded-2xl bg-[color:var(--cream-2)]">
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-2">Dog photo (JPG or PNG — up to 6MB)</label>
              {kennelFor?.kennel_card?.has_photo && !kennelPhoto && (
                <div className="mb-2 text-xs text-[color:var(--ink)]"><span className="font-semibold">A photo is already attached.</span> Choosing a new file will replace it.</div>
              )}
              <input
                data-testid="admin-kennel-photo-input"
                type="file"
                accept="image/*"
                onChange={(e)=>setKennelPhoto(e.target.files?.[0] || null)}
                className="w-full text-sm"
              />
              {kennelPhoto && <div className="mt-2 text-xs text-[color:var(--ink)]"><span className="font-semibold">Ready to upload:</span> {kennelPhoto.name} ({Math.round(kennelPhoto.size/1024)} KB)</div>}
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="admin-kennel-clear-btn"
              type="button"
              onClick={async () => {
                if (!kennelFor) return;
                if (!window.confirm("Remove the kennel card from this shift?")) return;
                setKennelBusy(true);
                try {
                  await api.delete(`/admin/shifts/${kennelFor.shift_id}/kennel-card`);
                  toast.success("Kennel card removed.");
                  setKennelFor(null); setKennelPhoto(null); await load();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setKennelBusy(false); }
              }}
              disabled={kennelBusy || (!kennelFor?.kennel_card?.dog_name && !kennelFor?.kennel_card?.has_photo)}
              className="btn-pill text-sm bg-[color:var(--red)]/10 text-[color:var(--red)] hover:bg-[color:var(--red)]/20 disabled:opacity-50"
            >Clear</button>
            <button type="button" onClick={()=>{ setKennelFor(null); setKennelPhoto(null); }} className="btn-pill btn-ghost-ink text-sm">Cancel</button>
            <button
              data-testid="admin-kennel-save-btn"
              disabled={kennelBusy}
              onClick={async () => {
                if (!kennelFor) return;
                setKennelBusy(true);
                try {
                  const fd = new FormData();
                  fd.append("dog_name", kennelForm.dog_name || "");
                  fd.append("dog_notes", kennelForm.dog_notes || "");
                  if (kennelPhoto) fd.append("photo", kennelPhoto);
                  await api.patch(`/admin/shifts/${kennelFor.shift_id}/kennel-card`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success("Kennel card saved.");
                  setKennelFor(null); setKennelPhoto(null); await load();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setKennelBusy(false); }
              }}
              className="btn-pill btn-primary text-sm disabled:opacity-60"
            >{kennelBusy ? "Saving…" : "Save kennel card"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paper waiver dialog for under-18 volunteers */}
      <Dialog open={!!paperWaiverFor} onOpenChange={(o)=>{ if(!o){ setPaperWaiverFor(null); setPaperWaiverScan(null); } }}>
        <DialogContent data-testid="admin-paper-waiver-dialog" className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Mark paper waiver received</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[color:var(--stone)]">
              Confirming will clear <span className="font-semibold text-[color:var(--ink)]">{paperWaiverFor?.name}</span> to sign up for shifts. Attach the scanned paper waiver so it&rsquo;s stored on their record (optional, but recommended).
            </p>
            <div className="p-3 rounded-2xl bg-[color:var(--cream-2)]">
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-2">Scanned waiver (PDF, JPG or PNG — up to 8MB)</label>
              <input
                data-testid="admin-paper-waiver-scan-input"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e)=>setPaperWaiverScan(e.target.files?.[0] || null)}
                className="w-full text-sm"
              />
              {paperWaiverScan && (
                <div className="mt-2 text-xs text-[color:var(--ink)]">
                  <span className="font-semibold">Ready to upload:</span> {paperWaiverScan.name} ({Math.round(paperWaiverScan.size / 1024)} KB)
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={()=>{ setPaperWaiverFor(null); setPaperWaiverScan(null); }} className="btn-pill btn-ghost-ink text-sm">Cancel</button>
            <button
              data-testid="admin-paper-waiver-confirm-btn"
              disabled={paperWaiverBusy}
              onClick={async () => {
                if (!paperWaiverFor) return;
                setPaperWaiverBusy(true);
                try {
                  const fd = new FormData();
                  fd.append("received", "true");
                  if (paperWaiverScan) fd.append("scan", paperWaiverScan);
                  const { data } = await api.post(
                    `/admin/volunteers/${paperWaiverFor.user_id}/mark-paper-waiver`,
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" } }
                  );
                  toast.success(data.scan_uploaded ? "Paper waiver saved with scanned copy." : "Paper waiver marked received.");
                  setPaperWaiverFor(null); setPaperWaiverScan(null);
                  await load();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setPaperWaiverBusy(false); }
              }}
              className="btn-pill btn-primary text-sm disabled:opacity-60"
            >
              {paperWaiverBusy ? "Saving…" : "Confirm & clear volunteer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit waiver dialog */}
      <Dialog open={waiverOpen} onOpenChange={setWaiverOpen}>
        <DialogContent data-testid="admin-waiver-dialog" className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit volunteer waiver</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[color:var(--stone)]">
              Paste your shelter&rsquo;s official waiver language below. Every volunteer must check each clause and type their name to sign. Existing signed waivers stay valid; new volunteers see the updated text.
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1 ml-1">Waiver title</label>
              <input data-testid="waiver-title-input" value={waiver.title} onChange={(e)=>setWaiver({...waiver, title: e.target.value})} placeholder="e.g. Volunteer Liability Waiver" className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1 ml-1">Intro paragraph</label>
              <textarea data-testid="waiver-intro-input" value={waiver.intro} onChange={(e)=>setWaiver({...waiver, intro: e.target.value})} placeholder="One or two sentences shown above the clauses." rows={2} className="w-full px-4 py-3 rounded-2xl border-2 border-[color:var(--border)] outline-none bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1 ml-1">Reference PDF (optional)</label>
              <div className="flex items-center gap-2 flex-wrap p-3 rounded-2xl bg-[color:var(--cream-2)]">
                {waiver.has_reference_pdf ? (
                  <>
                    <div className="flex items-center gap-2 text-sm flex-1 min-w-0">
                      <FileText className="h-4 w-4 text-[color:var(--red)] shrink-0" />
                      <span className="truncate font-semibold" data-testid="admin-waiver-pdf-name">{waiver.reference_pdf_filename || "waiver.pdf"}</span>
                    </div>
                    <label className="btn-pill btn-ghost-ink text-xs cursor-pointer">
                      Replace
                      <input data-testid="admin-waiver-pdf-input" type="file" accept="application/pdf" onChange={uploadWaiverPdf} className="hidden" />
                    </label>
                    <button data-testid="admin-waiver-pdf-remove" type="button" onClick={removeWaiverPdf} disabled={busy} className="btn-pill text-xs bg-[color:var(--red)]/10 text-[color:var(--red)] hover:bg-[color:var(--red)]/20">Remove</button>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-[color:var(--stone)] flex-1">Upload a full PDF of your shelter&rsquo;s waiver — volunteers can view it while signing.</span>
                    <label className="btn-pill btn-primary text-xs cursor-pointer">
                      Upload PDF
                      <input data-testid="admin-waiver-pdf-input" type="file" accept="application/pdf" onChange={uploadWaiverPdf} className="hidden" />
                    </label>
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1 ml-1">Clauses — one per line</label>
              <textarea data-testid="waiver-clauses-input" value={waiver.clausesText} onChange={(e)=>setWaiver({...waiver, clausesText: e.target.value})} rows={12} placeholder="I understand that working with shelter dogs involves risks…&#10;I release Hood River Adopt A Dog, its staff and volunteers from any liability…&#10;I will follow all shelter protocols…" className="w-full px-4 py-3 rounded-2xl border-2 border-[color:var(--border)] outline-none bg-white font-mono text-sm" />
              <div className="text-xs text-[color:var(--stone)] mt-1 ml-1">
                {waiver.clausesText.split("\n").filter(s => s.trim()).length} clause{waiver.clausesText.split("\n").filter(s => s.trim()).length === 1 ? "" : "s"} · Each becomes a required checkbox on the volunteer waiver page and PDF.
              </div>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={()=>setWaiverOpen(false)} className="btn-pill btn-ghost-ink text-sm">Cancel</button>
            <button data-testid="admin-waiver-save-btn" onClick={saveWaiver} disabled={busy} className="btn-pill btn-primary text-sm">{busy ? "Saving…" : "Save waiver"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create shift dialog */}
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent data-testid="admin-create-shift-dialog" className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New shift</DialogTitle>
          </DialogHeader>
          <form onSubmit={createShift} className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-2 ml-1">
                <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)]">Shift type</label>
                <span className="text-[10px] text-[color:var(--stone)]">Tap a time to edit</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {["morning1", "morning2", "evening", "custom"].map((k) => {
                  const active = form.slot === k;
                  const p = k === "custom" ? null : presets[k];
                  return (
                    <div key={k} className={`px-3 py-3 rounded-2xl border-2 text-left transition-colors ${active ? "border-[color:var(--red)] bg-[color:var(--red)]/5" : "border-[color:var(--border)]"}`}>
                      <button
                        type="button"
                        data-testid={`shift-slot-${k}`}
                        onClick={()=>setForm({...form, slot: k, role: (k === "custom" ? form.role : (presets[k]?.role || form.role))})}
                        className="w-full text-left"
                      >
                        <div className="text-[10px] uppercase tracking-wider text-[color:var(--stone)]">{SLOT_LABELS[k]}</div>
                        {k === "custom" ? (
                          <div className="font-display text-xs sm:text-sm mt-0.5 leading-tight">Custom event</div>
                        ) : (
                          <div className="font-display text-xs sm:text-sm mt-0.5 leading-tight" data-testid={`slot-${k}-label`}>
                            {p.title}
                          </div>
                        )}
                      </button>
                      {k !== "custom" && (
                        <div className="mt-2 space-y-1" onClick={(e)=>e.stopPropagation()}>
                          <input
                            type="text"
                            data-testid={`slot-${k}-title`}
                            value={p.title}
                            disabled={presetsBusy}
                            onChange={(e) => setPresets({ ...presets, [k]: { ...p, title: e.target.value } })}
                            onBlur={(e) => { if (e.target.value.trim() && e.target.value !== presets[k].title) savePresets({ ...presets, [k]: { ...p, title: e.target.value.trim() } }); }}
                            placeholder="Shift name"
                            className="w-full px-2 py-1 text-[11px] rounded-md border border-[color:var(--border)] bg-white"
                            aria-label={`${k} title`}
                          />
                          <div className="flex items-center gap-1">
                            <input
                              type="time"
                              data-testid={`slot-${k}-start`}
                              value={p.start_time}
                              disabled={presetsBusy}
                              onChange={(e) => setPresets({ ...presets, [k]: { ...p, start_time: e.target.value } })}
                              onBlur={(e) => { if (e.target.value !== p.start_time) return; savePresets({ ...presets, [k]: { ...p, start_time: e.target.value } }); }}
                              className="w-full px-1.5 py-1 text-[10px] rounded-md border border-[color:var(--border)] bg-white"
                              aria-label={`${k} start`}
                            />
                            <input
                              type="time"
                              data-testid={`slot-${k}-end`}
                              value={p.end_time}
                              disabled={presetsBusy}
                              onChange={(e) => setPresets({ ...presets, [k]: { ...p, end_time: e.target.value } })}
                              onBlur={(e) => { if (e.target.value !== p.end_time) return; savePresets({ ...presets, [k]: { ...p, end_time: e.target.value } }); }}
                              className="w-full px-1.5 py-1 text-[10px] rounded-md border border-[color:var(--border)] bg-white"
                              aria-label={`${k} end`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-[color:var(--stone)] mt-2 ml-1">You can rename or re-time any slot. New shifts created after saving will use these values, and the calendar chip labels update immediately.</p>
            </div>

            {form.slot === "custom" && (
              <div className="space-y-2 p-4 rounded-2xl bg-[color:var(--cream-2)]" data-testid="custom-shift-fields">
                <input data-testid="custom-title-input" required placeholder="Special event title (e.g. Fall Adoption Fair)" value={form.custom_title} onChange={(e)=>setForm({...form, custom_title: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Event starts</label>
                    <input data-testid="custom-start-input" required type="time" value={form.custom_start} onChange={(e)=>setForm({...form, custom_start: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Event ends</label>
                    <input data-testid="custom-end-input" required type="time" value={form.custom_end} onChange={(e)=>setForm({...form, custom_end: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[color:var(--stone)] ml-1">Split into shorter sign-up slots?</label>
                  <select
                    data-testid="custom-split-select"
                    value={form.event_split_minutes}
                    onChange={(e) => setForm({ ...form, event_split_minutes: Number(e.target.value) })}
                    className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white"
                  >
                    <option value={0}>No — one single shift covering the whole event</option>
                    <option value={30}>30-minute slots</option>
                    <option value={60}>1-hour slots</option>
                    <option value={90}>1½-hour slots</option>
                    <option value={120}>2-hour slots</option>
                    <option value={180}>3-hour slots</option>
                    <option value={240}>4-hour slots</option>
                  </select>
                  {Number(form.event_split_minutes) > 0 && (() => {
                    // Preview the sub-shifts the admin will create so there are no surprises.
                    const [sh, sm] = (form.custom_start || "0:0").split(":").map(Number);
                    const [eh, em] = (form.custom_end || "0:0").split(":").map(Number);
                    const totalMin = (eh * 60 + em) - (sh * 60 + sm);
                    const slot = Number(form.event_split_minutes);
                    if (totalMin <= 0 || slot <= 0 || slot >= totalMin) return null;
                    const slots = [];
                    let curMin = sh * 60 + sm;
                    const endMin = eh * 60 + em;
                    while (curMin + slot <= endMin) {
                      const a = `${String(Math.floor(curMin/60)).padStart(2,"0")}:${String(curMin%60).padStart(2,"0")}`;
                      const b = `${String(Math.floor((curMin+slot)/60)).padStart(2,"0")}:${String((curMin+slot)%60).padStart(2,"0")}`;
                      slots.push(`${fmt12(a)}–${fmt12(b)}`);
                      curMin += slot;
                    }
                    return (
                      <p className="mt-2 ml-1 text-[11px] text-[color:var(--stone)]" data-testid="custom-split-preview">
                        We&rsquo;ll create <span className="font-semibold text-[color:var(--ink)]">{slots.length} sign-up {slots.length === 1 ? "slot" : "slots"}</span>: {slots.join(" · ")}
                      </p>
                    );
                  })()}
                </div>
              </div>
            )}

            <select data-testid="shift-role-select" value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none focus:border-[color:var(--ink)] bg-white">
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            <input data-testid="shift-date-input" required type="date" value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
            <input data-testid="shift-location-input" required placeholder="Location" value={form.location} onChange={(e)=>setForm({...form, location: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
            <input data-testid="shift-max-input" required type="number" min="1" placeholder="Max volunteers" value={form.max_volunteers} onChange={(e)=>setForm({...form, max_volunteers: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />

            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1 ml-1">Repeat</label>
              <div className="grid grid-cols-3 gap-2">
                <select data-testid="shift-repeat-freq" value={form.repeat_frequency} onChange={(e)=>setForm({...form, repeat_frequency: e.target.value})} className="col-span-2 px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white">
                  <option value="once">Just this date</option>
                  <option value="daily">Every day</option>
                  <option value="weekly">Every week</option>
                </select>
                <input data-testid="shift-repeat-count" type="number" min="1" max="366" disabled={form.repeat_frequency === "once"} value={form.repeat_count} onChange={(e)=>setForm({...form, repeat_count: e.target.value})} className="px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white disabled:opacity-40" />
              </div>
              {form.repeat_frequency !== "once" && (
                <div className="text-xs text-[color:var(--stone)] mt-1 ml-1">
                  Creates {form.repeat_count} shift{Number(form.repeat_count) === 1 ? "" : "s"} starting {form.date || "the start date"}.
                </div>
              )}
            </div>

            <textarea data-testid="shift-description-input" placeholder="Description / task details (optional)" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="w-full px-4 py-3 rounded-2xl border-2 border-[color:var(--border)] outline-none bg-white" rows={2} />
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="shift-urgent-checkbox" type="checkbox" checked={form.urgent} onChange={(e)=>setForm({...form, urgent: e.target.checked})} className="h-4 w-4 accent-[color:var(--red)]" />
              Mark as urgent (highlighted for volunteers)
            </label>
            <DialogFooter>
              <button type="button" onClick={()=>setCreate(false)} className="btn-pill btn-ghost-ink text-sm">Cancel</button>
              <button data-testid="shift-create-submit-btn" type="submit" disabled={busy} className="btn-pill btn-primary text-sm">{busy ? "Creating…" : "Create shift"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk generate week dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="admin-bulk-generate-dialog" className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Generate a week (or more)</DialogTitle>
          </DialogHeader>
          <form onSubmit={bulkGenerate} className="space-y-3">
            <div className="text-sm text-[color:var(--stone)]">
              This creates the three daily shift slots you&rsquo;ve picked below for every day in the range. Duplicates are skipped automatically.
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1 ml-1">Start date</label>
              <input data-testid="bulk-start-date" required type="date" value={bulk.start_date} onChange={(e)=>setBulk({...bulk, start_date: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] block mb-1 ml-1">Number of days</label>
              <input data-testid="bulk-days" required type="number" min="1" max="366" value={bulk.days} onChange={(e)=>setBulk({...bulk, days: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
            </div>
            <select data-testid="bulk-role" value={bulk.role || DEFAULT_PRESET_ROLE} onChange={(e)=>setBulk({...bulk, role: e.target.value})} className="hidden">
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input data-testid="bulk-location" required placeholder="Location" value={bulk.location} onChange={(e)=>setBulk({...bulk, location: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
            <input data-testid="bulk-max" required type="number" min="1" placeholder="Max volunteers per shift" value={bulk.max_volunteers} onChange={(e)=>setBulk({...bulk, max_volunteers: e.target.value})} className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
            <div className="pt-1">
              <div className="text-[11px] uppercase tracking-wider text-[color:var(--stone)] mb-2 ml-1">Daily slots to generate</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[color:var(--cream-2)] cursor-pointer">
                  <input data-testid="bulk-include-morning1" type="checkbox" checked={bulk.include_morning1} onChange={(e)=>setBulk({...bulk, include_morning1: e.target.checked})} className="h-4 w-4 accent-[color:var(--red)]" />
                  <span>{presets.morning1?.title || "Morning 1"} <span className="text-[color:var(--stone)]">({fmt12(presets.morning1?.start_time)}–{fmt12(presets.morning1?.end_time)})</span></span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[color:var(--cream-2)] cursor-pointer">
                  <input data-testid="bulk-include-morning2" type="checkbox" checked={bulk.include_morning2} onChange={(e)=>setBulk({...bulk, include_morning2: e.target.checked})} className="h-4 w-4 accent-[color:var(--red)]" />
                  <span>{presets.morning2?.title || "Morning 2"} <span className="text-[color:var(--stone)]">({fmt12(presets.morning2?.start_time)}–{fmt12(presets.morning2?.end_time)})</span></span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[color:var(--cream-2)] cursor-pointer">
                  <input data-testid="bulk-include-evening" type="checkbox" checked={bulk.include_evening} onChange={(e)=>setBulk({...bulk, include_evening: e.target.checked})} className="h-4 w-4 accent-[color:var(--red)]" />
                  <span>{presets.evening?.title || "Evening"} <span className="text-[color:var(--stone)]">({fmt12(presets.evening?.start_time)}–{fmt12(presets.evening?.end_time)})</span></span>
                </label>
              </div>
              <p className="text-[11px] text-[color:var(--stone)] mt-2 ml-1">Need a one-off special event (fair, fundraiser, etc.)? Close this and use <span className="font-semibold">New shift → Special event</span>.</p>
            </div>
            <DialogFooter>
              <button type="button" onClick={()=>setBulkOpen(false)} className="btn-pill btn-ghost-ink text-sm">Cancel</button>
              <button data-testid="bulk-generate-submit-btn" type="submit" disabled={busy} className="btn-pill btn-primary text-sm">{busy ? "Generating…" : "Generate shifts"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Broadcast dialog */}
      <Dialog open={!!broadcast} onOpenChange={(v) => { if (!v) { setBroadcast(null); setBcMode("all"); setBcIncludeMe(true); setBcSelected(new Set()); } }}>
        <DialogContent data-testid="admin-broadcast-dialog" className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Notify volunteers</DialogTitle>
            <DialogDescription className="sr-only">Send a shift-fill email + in-app notification.</DialogDescription>
          </DialogHeader>
          {broadcast && (
            <div className="space-y-3">
              <div className="bg-[color:var(--cream-2)] rounded-2xl p-4 text-sm">
                <div className="font-semibold">{broadcast.title}</div>
                <div className="text-[color:var(--stone)]">{broadcast.date} · {fmt12(broadcast.start_time)}–{fmt12(broadcast.end_time)} · {broadcast.location}</div>
              </div>
              <input data-testid="broadcast-subject-input" value={msg.subject} onChange={(e)=>setMsg({...msg, subject: e.target.value})} placeholder="Subject" className="w-full px-4 py-3 rounded-full border-2 border-[color:var(--border)] outline-none bg-white" />
              <textarea data-testid="broadcast-message-input" value={msg.message} onChange={(e)=>setMsg({...msg, message: e.target.value})} placeholder="Message to volunteers" rows={4} className="w-full px-4 py-3 rounded-2xl border-2 border-[color:var(--border)] outline-none bg-white" />

              <div className="rounded-2xl border-2 border-[color:var(--border)] p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-[color:var(--stone)] mb-2">Recipients</div>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    data-testid="bc-mode-all"
                    onClick={()=>setBcMode("all")}
                    className={`btn-pill text-xs ${bcMode === "all" ? "btn-primary" : "btn-ghost-ink"}`}
                  >All volunteers &amp; admins</button>
                  <button
                    type="button"
                    data-testid="bc-mode-some"
                    onClick={()=>setBcMode("some")}
                    className={`btn-pill text-xs ${bcMode === "some" ? "btn-primary" : "btn-ghost-ink"}`}
                  >Pick specific people</button>
                </div>

                {bcMode === "some" && (
                  <div className="max-h-40 overflow-y-auto border border-[color:var(--border)] rounded-xl divide-y divide-[color:var(--border)]" data-testid="bc-picker">
                    {volunteers.map(v => (
                      <label key={v.user_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[color:var(--cream-2)]">
                        <input
                          type="checkbox"
                          data-testid={`bc-pick-${v.user_id}`}
                          checked={bcSelected.has(v.user_id)}
                          onChange={(e)=>{
                            const nx = new Set(bcSelected);
                            if (e.target.checked) nx.add(v.user_id); else nx.delete(v.user_id);
                            setBcSelected(nx);
                          }}
                          className="accent-[color:var(--red)]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="truncate flex items-center gap-1.5 flex-wrap">
                            <span className="truncate">{v.name}</span>
                            {renderInterests(v.interests)}
                            {renderSkills(v.skills)}
                          </div>
                          <div className="text-[10px] text-[color:var(--stone)] truncate">{v.email}</div>
                        </div>
                        {v.role === "admin" && <span className="tag-pill tag-red !text-[9px] !py-0">Admin</span>}
                        {v.email_unsubscribed && <span className="tag-pill !text-[9px] !py-0 bg-[color:var(--cream-2)] text-[color:var(--stone)]">Unsub</span>}
                      </label>
                    ))}
                    {volunteers.length === 0 && <div className="p-3 text-xs italic text-[color:var(--stone)]">No volunteers yet.</div>}
                  </div>
                )}

                <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="bc-include-me"
                    checked={bcIncludeMe}
                    onChange={(e)=>setBcIncludeMe(e.target.checked)}
                    className="accent-[color:var(--red)]"
                  />
                  <span>Send myself a copy</span>
                </label>
                <div className="text-[10px] text-[color:var(--stone)] mt-1">
                  Unsubscribed volunteers are always skipped. Everyone still gets an in-app notification.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={()=>setBroadcast(null)} className="btn-pill btn-ghost-ink text-sm">Cancel</button>
            <button
              data-testid="admin-broadcast-send-btn"
              onClick={sendBroadcast}
              disabled={busy || (bcMode === "some" && bcSelected.size === 0)}
              className="btn-pill btn-primary text-sm disabled:opacity-60"
            >
              <Send className="h-4 w-4 inline mr-1" /> {busy ? "Sending…" : "Send now"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
